import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { articles, CATEGORIES } from '../data/articles.js'
import ShareButtons from '../components/ShareButtons'
import SEO from '../components/SEO'

// Colore accento per ogni categoria (palette nerd)
const CAT_STYLE = {
  'Tecniche dagli Anime': { text: 'text-nerd-purple', border: 'border-nerd-purple/40', bg: 'bg-nerd-purple/10', dot: 'bg-nerd-purple' },
  'Crescita Personale': { text: 'text-emerald-400', border: 'border-emerald-400/40', bg: 'bg-emerald-400/10', dot: 'bg-emerald-400' },
  'Miglioramento Performance': { text: 'text-nerd-cyan', border: 'border-nerd-cyan/40', bg: 'bg-nerd-cyan/10', dot: 'bg-nerd-cyan' },
  'Salute & Biohacking': { text: 'text-amber-400', border: 'border-amber-400/40', bg: 'bg-amber-400/10', dot: 'bg-amber-400' },
}
const catStyle = (c) => CAT_STYLE[c] || CAT_STYLE['Crescita Personale']

export default function BlogPage() {
  const [query, setQuery] = useState('')
  const [activeCats, setActiveCats] = useState([])

  const toggleCat = (c) =>
    setActiveCats((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]))

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return articles.filter((a) => {
      const matchCat =
        activeCats.length === 0 || (a.categories || []).some((c) => activeCats.includes(c))
      if (!matchCat) return false
      if (!q) return true
      const hay = `${a.title} ${a.excerpt} ${(a.tags || []).join(' ')} ${(a.categories || []).join(' ')}`.toLowerCase()
      return hay.includes(q)
    })
  }, [query, activeCats])

  const hasFilters = activeCats.length > 0 || query.trim() !== ''

  return (
    <div className="min-h-screen px-4 pt-32 pb-24" style={{ background: '#050510' }}>
      <SEO
        title="Nerd Blog | Patch Notes per la Vita Reale"
        description="Guide, teorie e patch notes per il tuo sistema operativo umano. Scritte in linguaggio nerd e testate nel mondo reale su procrastinazione, sonno, stress e focus."
      />
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="font-display text-4xl md:text-5xl font-bold text-gradient mb-4">NERD BLOG</h1>
          <p className="text-nerd-gray text-lg max-w-2xl mx-auto leading-relaxed">
            Guide, teorie e patch notes per il tuo sistema operativo umano. Scritte in linguaggio nerd, testate nel mondo reale.
          </p>
        </motion.div>

        {/* ===== Barra di ricerca + filtri categoria ===== */}
        <div className="mb-10 rounded-2xl border border-nerd-purple/15 bg-nerd-dark/40 p-5 md:p-6">
          {/* Search */}
          <div className="relative mb-5">
            <svg className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-nerd-gray/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 18a7 7 0 110-14 7 7 0 010 14z" />
            </svg>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca un articolo, un tema, un anime..."
              className="w-full rounded-xl border border-nerd-purple/20 bg-nerd-dark/60 py-3 pl-12 pr-4 text-nerd-white placeholder-nerd-gray/40 outline-none transition-colors focus:border-nerd-cyan/60"
            />
          </div>

          {/* Categorie a caselle */}
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="mr-1 font-display text-xs uppercase tracking-widest text-nerd-gray/50">Categorie:</span>
            {CATEGORIES.map((c) => {
              const on = activeCats.includes(c)
              const st = catStyle(c)
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => toggleCat(c)}
                  aria-pressed={on}
                  className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm font-display transition-all ${
                    on ? `${st.border} ${st.bg} ${st.text}` : 'border-nerd-gray/20 text-nerd-gray/70 hover:border-nerd-gray/40'
                  }`}
                >
                  <span className={`h-2 w-2 rounded-full ${on ? st.dot : 'bg-nerd-gray/40'}`} />
                  {c}
                </button>
              )
            })}
            {hasFilters && (
              <button
                type="button"
                onClick={() => { setActiveCats([]); setQuery('') }}
                className="ml-1 text-xs text-nerd-gray/60 underline-offset-2 hover:text-nerd-cyan hover:underline"
              >
                Azzera filtri
              </button>
            )}
          </div>

          <p className="mt-4 text-xs text-nerd-gray/50">
            {filtered.length} {filtered.length === 1 ? 'articolo' : 'articoli'}
            {hasFilters ? ' trovati' : ' totali'}
          </p>
        </div>

        {/* ===== Griglia articoli ===== */}
        {filtered.length === 0 ? (
          <div className="rounded-2xl border border-nerd-purple/10 bg-nerd-dark/30 py-20 text-center">
            <p className="font-display text-lg text-nerd-white mb-2">Nessun articolo trovato</p>
            <p className="text-nerd-gray text-sm">Prova con un'altra parola chiave o cambia categoria.</p>
          </div>
        ) : (
          <div className="grid gap-8 md:grid-cols-2">
            {filtered.map((article, index) => (
              <motion.article
                key={article.slug}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: Math.min(index, 6) * 0.06 }}
                className="group relative rounded-xl overflow-hidden border border-nerd-purple/10 hover:border-nerd-cyan/30 transition-colors flex flex-col"
                style={{ background: 'rgba(10,10,30,0.6)' }}
              >
                <Link to={`/blog/${article.slug}`} className="block flex-1 flex flex-col">
                  {article.image && (
                    <div className="relative h-52 w-full overflow-hidden" style={{ borderBottom: '1px solid rgba(139,92,246,0.15)' }}>
                      <img src={article.image} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#050510]/80 to-transparent pointer-events-none" />
                    </div>
                  )}

                  <div className="p-6 md:p-8 flex-1 flex flex-col justify-between">
                    <div>
                      {/* Categorie */}
                      <div className="mb-3 flex flex-wrap gap-2">
                        {(article.categories || []).map((c) => {
                          const st = catStyle(c)
                          return (
                            <span key={c} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-display ${st.border} ${st.bg} ${st.text}`}>
                              <span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} />
                              {c}
                            </span>
                          )
                        })}
                      </div>

                      <div className="flex items-center gap-3 mb-4">
                        <span className="text-nerd-cyan text-xs font-display uppercase tracking-wider">{article.date}</span>
                        <span className="text-nerd-gray/50 text-xs">·</span>
                        <span className="text-nerd-gray/50 text-xs">{article.readTime} di lettura</span>
                      </div>

                      <h2 className="font-display text-xl md:text-2xl font-bold text-nerd-white mb-3 group-hover:text-nerd-cyan transition-colors leading-tight">
                        {article.title}
                      </h2>

                      <p className="text-nerd-gray text-sm leading-relaxed mb-6">{article.excerpt}</p>
                    </div>

                    <div>
                      <div className="flex flex-wrap gap-2">
                        {article.tags.map((tag) => (
                          <span key={tag} className="text-xs px-3 py-1 rounded-full border border-nerd-purple/20 text-nerd-gray/70">
                            {tag}
                          </span>
                        ))}
                      </div>

                      <div className="mt-6 flex items-center text-nerd-cyan text-sm font-display uppercase tracking-wider group-hover:translate-x-1 transition-transform">
                        Leggi l'articolo
                        <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </Link>
                <div className="px-6 md:px-8 pb-5 pt-4 border-t border-nerd-purple/10">
                  <ShareButtons compact title={article.title} url={`https://nerdcoach.it/blog/${article.slug}`} coverUrl={article.image} />
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
